import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash, Copy, PencilSimple, Check, LockKey } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * CustomerCredentialsDialog
 *
 * Pop-up to view, add, edit and delete a customer's "Saved Login IDs"
 * (site + user-id pairs — passwords are intentionally NOT stored).
 *
 * Mirrors the credentials sub-section inside `AddCustomerDialog` so the
 * editing experience is consistent between the new-customer flow and the
 * existing-customer quick-edit flow on the Invoice pages.
 *
 * Props:
 *   open         – controlled dialog visibility
 *   onOpenChange – setter for `open`
 *   customerId   – id of the customer whose credentials we're editing
 *   onSaved      – optional callback fired after a successful PUT, used by
 *                  callers that want to refresh their customer list cache.
 */
export default function CustomerCredentialsDialog({ open, onOpenChange, customerId, onSaved }) {
  const [customer, setCustomer] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [credForm, setCredForm] = useState({ site: '', user_id: '' });
  const [editIdx, setEditIdx] = useState(-1);
  const [saving, setSaving] = useState(false);

  // Reset + fetch every time the dialog opens for a (possibly different)
  // customer. We refuse to leak previous customer's creds into the new view.
  useEffect(() => {
    if (!open || !customerId) return;
    setCredentials([]);
    setCredForm({ site: '', user_id: '' });
    setEditIdx(-1);
    api.get(`/customers/${customerId}`)
      .then(r => {
        setCustomer(r.data);
        setCredentials(r.data?.credentials || []);
      })
      .catch(() => toast.error('Failed to load customer'));
  }, [open, customerId]);

  const addOrUpdateCred = () => {
    const site = (credForm.site || '').trim();
    const uid = (credForm.user_id || '').trim();
    if (!site || !uid) { toast.error('Site name and user id required'); return; }
    if (editIdx >= 0) {
      setCredentials(cs => cs.map((c, i) => i === editIdx ? { ...c, site, user_id: uid } : c));
    } else {
      setCredentials(cs => [...cs, { site, user_id: uid }]);
    }
    setCredForm({ site: '', user_id: '' });
    setEditIdx(-1);
  };

  const editCred = (i) => {
    setCredForm({ site: credentials[i].site, user_id: credentials[i].user_id });
    setEditIdx(i);
  };

  const removeCred = (i) => {
    setCredentials(cs => cs.filter((_, k) => k !== i));
    if (editIdx === i) { setCredForm({ site: '', user_id: '' }); setEditIdx(-1); }
  };

  const copyToClipboard = async (val) => {
    try { await navigator.clipboard.writeText(val); toast.success('Copied'); }
    catch { toast.error('Copy failed'); }
  };

  const save = async () => {
    if (!customer) return;
    setSaving(true);
    try {
      // PUT needs the full CustomerCreate payload — preserve name / phone /
      // address / notes verbatim and overlay only the credentials array.
      await api.put(`/customers/${customer.id}`, {
        name: customer.name,
        phone: customer.phone,
        address: customer.address || '',
        notes: customer.notes || '',
        email: customer.email || '',
        aadhar: customer.aadhar || '',
        credentials,
      });
      toast.success('Login IDs saved');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const formatModified = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-2xl" data-testid="customer-credentials-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LockKey size={18} className="text-[#3B82F6]" />
            Saved Login IDs
          </DialogTitle>
          <DialogDescription>
            {customer ? `${customer.name} — ${customer.phone}` : 'Loading customer...'}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] p-3" data-testid="credentials-section">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#52525B]">Site Name</Label>
              <Input
                value={credForm.site}
                onChange={e => setCredForm({ ...credForm, site: e.target.value })}
                placeholder="e.g. Aadhar Portal"
                className="rounded-lg mt-1 h-9"
                data-testid="ccd-site-input"
              />
            </div>
            <div className="flex-1">
              <Label className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#52525B]">User ID</Label>
              <Input
                value={credForm.user_id}
                onChange={e => setCredForm({ ...credForm, user_id: e.target.value })}
                placeholder="e.g. 1234-5678-9012"
                className="rounded-lg mt-1 h-9"
                data-testid="ccd-user-id-input"
              />
            </div>
            <Button
              type="button"
              onClick={addOrUpdateCred}
              className="rounded-lg h-9 px-3 text-xs bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white"
              data-testid="ccd-add-btn"
            >
              {editIdx >= 0 ? <><Check size={14} className="mr-1" /> Update</> : <><Plus size={14} className="mr-1" /> Add</>}
            </Button>
          </div>

          {credentials.length > 0 ? (
            <div className="mt-3 border-t border-[#E4E4E7] pt-2">
              <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 px-2 py-1 text-[9px] uppercase tracking-[0.12em] font-semibold text-[#A1A1AA]">
                <span>Site</span>
                <span>User ID</span>
                <span className="text-right">Modified</span>
                <span></span>
              </div>
              <div className="space-y-1">
                {credentials.map((c, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center text-xs px-2 py-1.5 rounded hover:bg-white"
                    data-testid={`ccd-row-${i}`}
                  >
                    <span className="font-semibold truncate">{c.site}</span>
                    <span className="font-mono text-[#0A0A0A] truncate">{c.user_id}</span>
                    <span className="text-[10px] text-[#71717A] whitespace-nowrap">{formatModified(c.modified_at)}</span>
                    <span className="flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => copyToClipboard(c.user_id)}
                        title="Copy user id"
                        className="text-[#52525B] hover:text-[#3B82F6] p-1"
                        data-testid={`ccd-copy-${i}`}
                      >
                        <Copy size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => editCred(i)}
                        title="Edit"
                        className="text-[#52525B] hover:text-[#3B82F6] p-1"
                        data-testid={`ccd-edit-${i}`}
                      >
                        <PencilSimple size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeCred(i)}
                        title="Delete"
                        className="text-[#EF4444] hover:text-[#DC2626] p-1"
                        data-testid={`ccd-delete-${i}`}
                      >
                        <Trash size={13} />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="mt-3 text-center text-xs text-[#A1A1AA] italic" data-testid="ccd-empty">
              No login IDs saved yet — add the first one above.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-xl"
            data-testid="ccd-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || !customer}
            className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl disabled:opacity-50"
            data-testid="ccd-save-btn"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
